import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Download, AlertTriangle } from 'lucide-react';
import { AppUpdateInfo } from '@nusrif/services';

interface AppUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  updateInfo: AppUpdateInfo;
  onUpdate: () => Promise<void>;
  isForceUpdate?: boolean;
}

export const AppUpdateModal: React.FC<AppUpdateModalProps> = ({
  isOpen,
  onClose,
  updateInfo,
  onUpdate,
  isForceUpdate = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Block all interactions when force update modal is open
  useEffect(() => {
    if (isOpen && isForceUpdate) {
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      // Do NOT disable body pointer events; it also disables the modal's button.
      
      // Prevent escape key from closing modal
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown, true);
      
      return () => {
        document.body.style.overflow = '';
        // No pointer-events reset needed
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [isOpen, isForceUpdate]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await onUpdate();
    } catch (error) {
      console.error('Failed to open store:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 animate-fade-in"
      onClick={isForceUpdate ? (e) => e.stopPropagation() : undefined}
    >
      {/* Modal Container */}
      <div 
        className="w-full max-w-sm mx-4 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="bg-white rounded-2xl shadow-2xl border-0 overflow-hidden">
          {/* Header with Icon */}
          <CardHeader className={`text-center pb-6 ${
            isForceUpdate 
              ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' 
              : 'bg-gradient-to-br from-primary-blue to-blue-600 text-white'
          }`}>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                {isForceUpdate ? (
                  <AlertTriangle className="w-8 h-8 text-white" />
                ) : (
                  <Download className="w-8 h-8 text-white" />
                )}
              </div>
            </div>
            <h2 className="text-xl font-bold mb-2">
              {isForceUpdate ? 'Update Required' : 'Update Available'}
            </h2>
            <p className="text-white/90 text-sm">
              To get the latest features and updates for a great experience,
              please update to continue using the app.
            </p>
          </CardHeader>

          {/* Content */}
          <CardContent className="p-6">
            {/* Version Information */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Current Version</span>
                <span className="text-sm font-medium text-gray-900">{updateInfo.currentVersion}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-600">Latest Version</span>
                <span className="text-sm font-medium text-blue-900">{updateInfo.latestVersion}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                className={`w-full ${
                  isForceUpdate 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-primary-blue hover:bg-blue-700'
                }`}
              >
                {isUpdating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Opening Store...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Update Now
                  </div>
                )}
              </Button>

              {!isForceUpdate && (
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="w-full"
                >
                  Maybe Later
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}; 